import React, { useMemo, useState } from 'react';
import { buildDriverDisplayName, buildTransportDisplayName, buildTransportMeta } from '../../logistics/components/driverUtils';

const StatCard = ({ label, value, hint, tone = 'slate' }) => {
  const tones = {
    slate: 'from-slate-900 via-slate-800 to-slate-900 text-white shadow-slate-300/40',
    blue: 'from-blue-600 via-cyan-500 to-sky-500 text-white shadow-cyan-300/40',
    emerald: 'from-emerald-600 via-teal-500 to-cyan-500 text-white shadow-emerald-300/40',
    rose: 'from-rose-500 via-red-500 to-orange-500 text-white shadow-rose-300/40'
  };

  return (
    <div className={`rounded-[26px] bg-gradient-to-br p-4 shadow-xl ${tones[tone] || tones.slate}`}>
      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-white/70">{label}</p>
      <p className="mt-3 text-3xl font-black tracking-[-0.04em]">{value}</p>
      {hint ? <p className="mt-2 text-xs text-white/80">{hint}</p> : null}
    </div>
  );
};



const getHistoryDriverLabel = (route) =>
  buildDriverDisplayName(
    {
      driverName: route?.driverName,
      driverLastName: route?.driverLastName,
      currentDriverName: route?.currentDriverName,
      currentDriverLastName: route?.currentDriverLastName,
      driverDisplayName: route?.driverDisplayName,
      choferName: route?.choferName
    },
    'Chofer no registrado'
  );

const getHistoryTransportLabel = (route) =>
  buildTransportDisplayName(
    {
      transportName: route?.transportName,
      transporterName: route?.transporterName,
      transportLabel: route?.transportLabel,
      nameOfTransport: route?.nameOfTransport,
      transportModel: route?.transportModel,
      vehicleLabel: route?.vehicleLabel,
      patent: route?.patent,
      vehiclePatent: route?.vehiclePatent,
      transportType: route?.transportType,
      transport: route?.transport
    },
    'Unidad no registrada'
  );

const getHistoryTransportMeta = (route) =>
  buildTransportMeta({
    transportName: route?.transportName,
    transporterName: route?.transporterName,
    transportModel: route?.transportModel,
    vehicleLabel: route?.vehicleLabel,
    patent: route?.patent,
    vehiclePatent: route?.vehiclePatent,
    transportType: route?.transportType
  });

const getHistoryServiceMeta = (route) => {
  const linkedDestinations = Array.isArray(route?.destinations)
    ? route.destinations.filter((destination) => Boolean(destination?.serviceLinked || destination?.serviceId))
    : [];

  if (linkedDestinations.length === 0) return null;

  const firstLinked = linkedDestinations[0] || {};
  const modeLabel = String(firstLinked?.serviceModeLabel || firstLinked?.serviceMode || '').trim();
  const serviceCount = new Set(
    linkedDestinations
      .map((destination) => String(destination?.serviceId || '').trim())
      .filter(Boolean)
  ).size;

  return {
    label: modeLabel || 'Servicio vinculado',
    count: serviceCount || 1
  };
};

const getHistoryEvidenceMeta = (route) => {
  const destinations = Array.isArray(route?.destinations) ? route.destinations : [];
  const destinationsWithEvidence = destinations.filter(
    (destination) =>
      Boolean(String(destination?.photoURL || '').trim()) ||
      (Array.isArray(destination?.evidenceFiles) && destination.evidenceFiles.length > 0)
  );

  if (destinationsWithEvidence.length === 0) return null;

  const totalEvidenceFiles = destinationsWithEvidence.reduce((sum, destination) => {
    const evidenceCount = Array.isArray(destination?.evidenceFiles) && destination.evidenceFiles.length > 0
      ? destination.evidenceFiles.length
      : (String(destination?.photoURL || '').trim() ? 1 : 0);
    return sum + evidenceCount;
  }, 0);

  const latestDestination = [...destinationsWithEvidence].sort((a, b) => {
    const aTime = new Date(a?.latestEvidence?.capturedAt || a?.completedAt || 0).getTime();
    const bTime = new Date(b?.latestEvidence?.capturedAt || b?.completedAt || 0).getTime();
    return bTime - aTime;
  })[0];

  return {
    stopCount: destinationsWithEvidence.length,
    totalEvidenceFiles,
    latestDestinationName: latestDestination?.name || 'Destino con evidencia'
  };
};

const EmptyState = ({ Icon, hasFilter }) => (
  <div className="col-span-full rounded-[28px] border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
      <Icon
        path="M19.5 14.25v-2.625a3.375 3.375 0 00-1.028-2.426L13.5 4.5a3.375 3.375 0 00-2.386-.99H6.75A2.25 2.25 0 004.5 5.76v12.48a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25z"
        className="h-7 w-7 text-slate-500"
      />
    </div>

    <h4 className="mt-5 text-xl font-bold text-slate-800">{hasFilter ? 'No hay rutas para esta fecha' : 'Todavía no hay rutas en historial'}</h4>
    <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-slate-500">
      {hasFilter
        ? 'Probá con otra fecha o limpiá el filtro para volver a la vista completa del historial.'
        : 'Cuando las rutas se completen o se cancelen, van a quedar disponibles acá para revisión, balance y detalle.'}
    </p>
  </div>
);

export default function HistoryView({
  routes,
  services,
  loading,
  onShowDetail,
  settings,
  Icon,
  sortRoutesDesc,
  getRouteTotalDistance,
  onOpenRoute
}) {
  const [filterDate, setFilterDate] = useState('');

  const history = useMemo(() => {
    return sortRoutesDesc(
      (routes || []).filter((route) => ['completed', 'cancelled'].includes(route.status))
    );
  }, [routes, sortRoutesDesc]);

  const filteredHistory = useMemo(() => {
    if (!filterDate) return history;
    return history.filter((route) => route.date === filterDate);
  }, [filterDate, history]);

  const stats = useMemo(() => {
    const completed = filteredHistory.filter((route) => route.status === 'completed').length;
    const cancelled = filteredHistory.filter((route) => route.status === 'cancelled').length;
    const totalDistance = filteredHistory.reduce((acc, route) => {
      const rawDistance = getRouteTotalDistance(route, settings);
      const numericDistance = typeof rawDistance === 'number'
        ? rawDistance
        : parseFloat(String(rawDistance || '0').replace(',', '.'));
      return acc + (Number.isFinite(numericDistance) ? numericDistance : 0);
    }, 0);

    const latestRoute = filteredHistory[0] || null;

    return {
      totalRoutes: filteredHistory.length,
      completed,
      cancelled,
      totalDistance,
      latestRoute
    };
  }, [filteredHistory, getRouteTotalDistance, settings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full rounded-[28px] border border-slate-200 bg-white shadow-sm">
        <Icon
          path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
          className="w-12 h-12 text-blue-500 animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pr-1">
      <div className="space-y-5">
        <section className="relative overflow-hidden rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.10),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.08),transparent_26%)]" />

          <div className="relative flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">Historial operativo</p>
              <h3 className="mt-2 text-[1.7rem] font-black tracking-[-0.04em] text-slate-900 md:text-[2.2rem]">
                Revisá cierres, cancelaciones y kilómetros
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                Explorá rutas finalizadas, detectá excepciones del día y abrí el detalle de cada hoja sin perder contexto.
              </p>
            </div>

            <div className="flex w-full max-w-md flex-col gap-2.5 sm:flex-row xl:w-auto xl:min-w-[22rem] xl:justify-end">
              <div className="flex-1">
                <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                  Filtrar por fecha
                </label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                />
              </div>

              <button
                type="button"
                onClick={() => setFilterDate('')}
                disabled={!filterDate}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 xl:self-end"
              >
                <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                Limpiar
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Rutas visibles" value={stats.totalRoutes} hint={filterDate ? 'Con filtro activo' : 'Vista histórica completa'} tone="slate" />
          <StatCard label="Completadas" value={stats.completed} hint="Cierres exitosos" tone="emerald" />
          <StatCard label="Canceladas" value={stats.cancelled} hint="Rutas con corte operativo" tone="rose" />
          <StatCard
            label="Km acumulados"
            value={stats.totalDistance.toFixed(1)}
            hint={stats.latestRoute ? `Última ruta: ${stats.latestRoute.name}` : 'Sin actividad reciente'}
            tone="blue"
          />
        </section>

        <section className="rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Rutas cerradas</p>
              <h4 className="mt-1.5 text-xl font-bold tracking-[-0.03em] text-slate-900">Historial disponible</h4>
              <p className="mt-1.5 max-w-2xl text-sm text-slate-500">
                Cada fila conserva el acceso al detalle para revisar destinos, estados y cierre operativo.
              </p>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <Icon path="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" className="h-4 w-4" />
              <span>{filteredHistory.length} rutas en pantalla</span>
            </div>
          </div>

          {filteredHistory.length === 0 ? (
            <EmptyState Icon={Icon} hasFilter={Boolean(filterDate)} />
          ) : (
            <>
              <div className="space-y-3 md:hidden">
                {filteredHistory.map((route) => {
                  const kmTotales = getRouteTotalDistance(route, settings);
                  const serviceMeta = getHistoryServiceMeta(route);
                  const evidenceMeta = getHistoryEvidenceMeta(route);
                  const isCompleted = route.status === 'completed';
                  const statusText = isCompleted ? 'Completado' : 'Cancelado';
                  const statusClass = isCompleted
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-rose-200 bg-rose-50 text-rose-700';

                  return (
                    <div key={route.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                            {new Date(`${route.date}T00:00:00`).toLocaleDateString('es-AR')}
                          </p>
                          <h5 className="mt-2 text-lg font-bold text-slate-900">{route.name}</h5>
                          {serviceMeta ? (
                            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-bold text-violet-700">
                              <span>{serviceMeta.label}</span>
                              <span className="text-violet-400">·</span>
                              <span>{serviceMeta.count} servicio{serviceMeta.count === 1 ? '' : 's'}</span>
                            </div>
                          ) : null}
                          {evidenceMeta ? (
                            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[11px] font-bold text-cyan-700">
                              <span>{evidenceMeta.totalEvidenceFiles} evidencia{evidenceMeta.totalEvidenceFiles === 1 ? '' : 's'}</span>
                              <span className="text-cyan-400">·</span>
                              <span>{evidenceMeta.latestDestinationName}</span>
                            </div>
                          ) : null}
                        </div>
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusClass}`}>
                          {statusText}
                        </span>
                      </div>

                      <div className="mt-4 rounded-2xl bg-white px-3 py-3">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Chofer</p>
                            <p className="mt-1 text-sm font-bold text-slate-900">{getHistoryDriverLabel(route)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Unidad</p>
                            <p className="mt-1 text-sm font-bold text-slate-900">{getHistoryTransportLabel(route)}</p>
                            <p className="mt-1 text-xs text-slate-500">{getHistoryTransportMeta(route) || 'Sin detalle adicional'}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Km totales</p>
                            <p className="mt-1 text-lg font-bold text-slate-900">{kmTotales}</p>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                          {typeof onOpenRoute === 'function' ? (
                            <button
                              type="button"
                              onClick={() => onOpenRoute(route)}
                              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
                            >
                              <Icon path="M3.75 5.25h16.5M3.75 12h16.5m-16.5 6.75h16.5" className="h-4 w-4" />
                              Ir a la ruta
                            </button>
                          ) : null}

                          <button
                            type="button"
                            onClick={() => onShowDetail(route)}
                            className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700"
                          >
                            <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
                            Ver detalle
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden overflow-hidden rounded-[24px] border border-slate-200 md:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr>
                        <th className="py-3 px-4 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Fecha</th>
                        <th className="py-3 px-4 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Hoja</th>
                        <th className="py-3 px-4 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Estado</th>
                        <th className="py-3 px-4 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Chofer</th>
                        <th className="py-3 px-4 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Unidad</th>
                        <th className="py-3 px-4 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Km totales</th>
                        <th className="py-3 px-4 text-right text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {filteredHistory.map((route) => {
                        const kmTotales = getRouteTotalDistance(route, settings);
                        const serviceMeta = getHistoryServiceMeta(route);
                        const evidenceMeta = getHistoryEvidenceMeta(route);
                        const isCompleted = route.status === 'completed';
                        const statusText = isCompleted ? 'Completado' : 'Cancelado';
                        const statusClass = isCompleted
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-rose-200 bg-rose-50 text-rose-700';

                        return (
                          <tr key={route.id} className="transition hover:bg-slate-50/80">
                            <td className="py-4 px-4 text-sm text-slate-600">
                              {new Date(`${route.date}T00:00:00`).toLocaleDateString('es-AR')}
                            </td>
                            <td className="py-4 px-4 text-sm text-slate-900">
                              <div className="font-semibold">{route.name}</div>
                              {serviceMeta ? (
                                <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-violet-700">
                                  <span>{serviceMeta.label}</span>
                                  <span>{serviceMeta.count}</span>
                                </div>
                              ) : null}
                              {evidenceMeta ? (
                                <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-cyan-700">
                                  <span>{evidenceMeta.totalEvidenceFiles} evid.</span>
                                  <span>{evidenceMeta.latestDestinationName}</span>
                                </div>
                              ) : null}
                            </td>
                            <td className="py-4 px-4 text-sm">
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${statusClass}`}>
                                {statusText}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-slate-900"><div className="font-semibold">{getHistoryDriverLabel(route)}</div></td>
                            <td className="py-4 px-4 text-sm text-slate-700"><div className="font-semibold text-slate-900">{getHistoryTransportLabel(route)}</div><div className="mt-1 text-xs text-slate-500">{getHistoryTransportMeta(route) || 'Sin detalle adicional'}</div></td>
                            <td className="py-4 px-4 text-sm font-bold text-slate-900">{kmTotales}</td>
                            <td className="py-4 px-4 text-right">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {typeof onOpenRoute === 'function' ? (
                                  <button
                                    type="button"
                                    onClick={() => onOpenRoute(route)}
                                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
                                  >
                                    <Icon path="M3.75 5.25h16.5M3.75 12h16.5m-16.5 6.75h16.5" className="h-4 w-4" />
                                    Ir a la ruta
                                  </button>
                                ) : null}

                                <button
                                  type="button"
                                  onClick={() => onShowDetail(route)}
                                  className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
                                >
                                  <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
                                  Ver detalle
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
